import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AiReportScheduleModal } from '@/components/reports/AiReportScheduleModal';
import { invalidateMyReports } from '@/hooks/useMyReports';
import { AiReportShareModal } from '@/components/reports/AiReportShareModal';
import { usePageContext } from '@/hooks/usePageContext';
import { aiReportsApi } from '@/services/aiReports.service';
import { getErrorMessage } from '@/services/apiClient';

/**
 * Manage one AI report schedule (the target of the Assistant's "Manage
 * schedule" link and the Reports list "Manage" action): cadence, next/last run,
 * and — for the owner — pause/resume, run-now, edit, share, delete.
 */
export default function AiReportScheduleViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scheduleId = Number(id);

  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const query = useQuery({
    queryKey: ['ai-report-schedule', scheduleId],
    queryFn: () => aiReportsApi.schedules.get(scheduleId),
    enabled: Number.isFinite(scheduleId) && scheduleId > 0,
  });
  const s = query.data;

  const updateAIContext = usePageContext('Scheduled Report');
  useEffect(() => {
    if (s) updateAIContext({ dataSummary: `Scheduled report "${s.title ?? s.prompt}" — ${s.cadenceLabel ?? ''}` });
  }, [s, updateAIContext]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ai-report-schedule', scheduleId] });
    invalidateMyReports(queryClient);
  };

  const pauseResume = useMutation({
    mutationFn: () => (s?.active ? aiReportsApi.schedules.pause(scheduleId) : aiReportsApi.schedules.resume(scheduleId)),
    onSuccess: invalidate,
  });
  const runNow = useMutation({
    mutationFn: () => aiReportsApi.schedules.runNow(scheduleId),
    onSuccess: (r) => { invalidate(); navigate(`/reports/ai-results/${r.reportId}`); },
  });
  const remove = useMutation({
    mutationFn: () => aiReportsApi.schedules.remove(scheduleId),
    onSuccess: () => {
      invalidateMyReports(queryClient);
      navigate(-1);
    },
  });

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <PageHeader
        title="Scheduled report"
        subtitle={s?.title || s?.prompt || 'AI report schedule'}
      />

      {query.isError && (
        <Alert variant="error">{getErrorMessage(query.error, 'Failed to load this schedule.')}</Alert>
      )}

      {s && (
        <Card className="max-w-2xl space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={s.active ? 'success' : 'neutral'}>{s.active ? 'Active' : 'Paused'}</Badge>
            {s.recurring ? <Badge variant="info">Recurring</Badge> : <Badge variant="purple">One-time</Badge>}
            {!s.isOwner && <Badge variant="neutral">Shared with you</Badge>}
          </div>

          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <Field label="Schedule" value={s.cadenceLabel ?? '—'} />
            <Field label="Next run" value={s.nextRunLocal ?? (s.active ? '—' : 'Paused')} />
            <Field label="Timezone" value={s.timezone} />
            <Field label="Times run" value={String(s.runCount)} />
            <Field label="Created by" value={s.generatedBy} />
          </dl>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Report request</div>
            <div className="text-sm text-slate-700">{s.prompt}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            {s.lastReportRunId ? (
              <Button variant="secondary" onClick={() => navigate(`/reports/ai-results/${s.lastReportRunId}`)}>
                Open latest result
              </Button>
            ) : (
              <span className="text-sm text-slate-500">No runs yet.</span>
            )}

            {s.isOwner && (
              <>
                <Button variant="secondary" onClick={() => runNow.mutate()} loading={runNow.isPending}>
                  Run now
                </Button>
                <Button variant="secondary" onClick={() => pauseResume.mutate()} loading={pauseResume.isPending}>
                  {s.active ? 'Pause' : 'Resume'}
                </Button>
                <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>
                <Button variant="secondary" onClick={() => setShareOpen(true)}>Share</Button>
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>Delete</Button>
              </>
            )}
          </div>
        </Card>
      )}

      {s?.isOwner && (
        <>
          <AiReportScheduleModal
            isOpen={editOpen}
            onClose={() => setEditOpen(false)}
            scheduleId={scheduleId}
            onSaved={invalidate}
          />
          <AiReportShareModal
            isOpen={shareOpen}
            onClose={() => setShareOpen(false)}
            targetType="schedule"
            targetId={scheduleId}
            targetTitle={s.title || s.prompt}
          />
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete schedule"
        message="Delete this scheduled report? Reports it has already generated are kept, but it will stop running."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  );
}
